/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import cashService from 'service/cash';

interface Bonus {
  name: string;
}

interface CreateCashRegisterProps {
  setOpenCash: (show: boolean) => void;
  selectedCashId?: string;
  refetch?: () => void;
  setSelectedCashId?: (id: string) => void;
  bonusData?: Bonus[];
}

const CreateCashRegister = ({
  setOpenCash,
  selectedCashId,
  refetch,
  setSelectedCashId,
  bonusData = []
}: CreateCashRegisterProps) => {
  const { t } = useTranslation();
  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>();
  const hasFetchedData = useRef(false);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm();

  const statusOptions = useMemo(
    () => [
      {
        value: true,
        label: t('active')
      },
      {
        value: false,
        label: t('blocked')
      }
    ],
    [t]
  );

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => inputRef.current?.select(), 300);
    }
  };

  const fetchData = async () => {
    if (selectedCashId === '') return setLoading(false);

    cashService
      .getCashById(selectedCashId)
      .then((res: any) => {
        const computed = {
          name: res?.name,
          status: res?.status ?? false
        };
        reset(computed);
      })
      .finally(() => setLoading(false));
  };

  const convertStatus = (status: boolean | string): boolean =>
    status === 'true' || status === true;

  const onSubmit: SubmitHandler<FieldValues> = data => {
    if (isSubmitting) return; // Prevent additional submissions
    setIsSubmitting(true);

    const nameExists = bonusData.some(
      (bonus: any) => bonus.name === data?.name
    );

    if (nameExists) {
      dispatch(
        showAlert({
          title: t('name_already_exists')
        })
      );
      setIsSubmitting(false); // Allow retry
      return;
    }

    if (selectedCashId !== '') return update(data);
    createData(data);
  };

  const createData = (data: any) => {
    setLoading(true);

    const createdData = {
      name: data?.name,
      status: convertStatus(data.status)
    };

    cashService.createCash(createdData).then((res: any) => {
      if (res) {
        dispatch(
          showAlert({
            title: `${t('cash')} ${t('successfully_added')}`,
            type: 'success'
          })
        );
        refetch?.();
        setOpenCash(false);
        setIsSubmitting(false);
        setLoading(false);
        setSelectedCashId?.('');
      }
    });
  };

  const update = (data: any) => {
    setLoading(true);

    const updateData = {
      name: data?.name,
      status: convertStatus(data.status),
      id: selectedCashId
    };
    cashService.updateCash(selectedCashId, updateData).finally(() => {
      refetch?.();
      setLoading(false);
      setIsSubmitting(false);
      setOpenCash(false);
      setSelectedCashId?.('');
    });
  };

  useEffect(() => {
    if (selectedCashId !== '' && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [selectedCashId]);

  useEffect(() => {
    focusInput();
  }, []);

  return (
    <>
      <Form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Row className="mb-3">
          <Col>
            <Controller
              name={`name`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.Floating className="md-4">
                  <Form.Control
                    {...field}
                    ref={inputRef}
                    type="text"
                    placeholder={t('name_cash_desk')}
                    onFocus={e => e.target.select()}
                    isInvalid={!!errors.name}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('name_cash_desk')}
                  </label>
                  {errors.name && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <div className="react-select-container">
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field} isInvalid={!!errors.status}>
                      <option className="d-none" value=""></option>
                      {statusOptions?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">{t('status')}</label>
                    {errors?.status && (
                      <span
                        style={{
                          marginTop: '2px',
                          color: 'red',
                          fontSize: '12px'
                        }}
                      >
                        {t('required_field')}
                      </span>
                    )}
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="submit"
          loading={loading}
          loadingPosition="start"
          disabled={isSubmitting}
        >
          {t('add')}
        </Button>
      </Form>
    </>
  );
};

export default CreateCashRegister;
